import { Card, CardContent, Typography } from "@mui/material";
import { classes } from "portal/theme/theme";
import { DateTime } from "luxon";
import { getRobotPath } from "portal/utils/routing";
import { isEmpty } from "portal/utils/arrays";
import { Link } from "react-router-dom";
import { MuiIcon } from "common/components/types";
import { MuiTypographyColor, useMuiColorStyle } from "common/hooks/useMuiColor";
import {
  SummaryCardItem,
  SummaryCardItemProps,
} from "portal/components/SummaryCardItem";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";
import { withErrorBoundary } from "portal/components/ErrorBoundary";
import CheckinIcon from "@mui/icons-material/CellTowerOutlined";
import React, { Fragment, FunctionComponent, useMemo } from "react";

/**
 * information displayed in the robot summary in both the "cards" layout of the
 * fleet view, or on individual robot summary pages
 */
export interface RobotSummaryHeadlineInfo {
  StatusIcon: MuiIcon;
  statusColor: MuiTypographyColor;
  headlineText: string;
  checkedIn: DateTime | undefined;
  className?: string;
}

const _RobotSummaryIcon: FunctionComponent<RobotSummaryHeadlineInfo> = ({
  StatusIcon,
  statusColor,
  className = "",
}) => {
  // this isn't a <Typography> so we need to convert the color to a style if
  // it's a typography color
  const style = useMuiColorStyle(statusColor);

  return <StatusIcon className={className} style={style} />;
};

export const RobotSummaryIcon = withErrorBoundary({}, _RobotSummaryIcon);

const _RobotSummaryText: FunctionComponent<RobotSummaryHeadlineInfo> = ({
  statusColor,
  headlineText,
  className = "",
}) => {
  const colorStyle = useMuiColorStyle(statusColor);

  return (
    <span
      className={classes(
        "text-xs whitespace-nowrap overflow-ellipsis",
        className
      )}
      style={colorStyle}
    >
      {headlineText}
    </span>
  );
};

export const RobotSummaryText = withErrorBoundary(
  { i18nKey: "components.robots.RobotSummary.failed", small: true },
  _RobotSummaryText
);

const _RobotSummaryCheckedin: FunctionComponent<RobotSummaryHeadlineInfo> = ({
  checkedIn: checkin,
  className = "",
}) => {
  const { t, i18n } = useTranslation();
  const commonClasses = `flex items-center ${className}`;
  const now = DateTime.local();
  const icon = <CheckinIcon className="mr-2" />;

  let color: React.ComponentProps<typeof Typography>["color"];
  let text: string;

  if (checkin) {
    text = t("components.robots.RobotSummary.checkedIn.withTime", {
      time: checkin.toRelative({ locale: i18n.language }),
    });

    if (checkin > now.minus({ minutes: 30 })) {
      color = "success";
    } else if (checkin > now.minus({ days: 7 })) {
      color = "warning";
    } else {
      color = "error";
    }
  } else {
    color = "warning";
    text = t("components.robots.RobotSummary.checkedIn.never");
  }

  return (
    <Typography color={color} className={commonClasses}>
      {icon}
      {text}
    </Typography>
  );
};

export const RobotSummaryCheckedin = withErrorBoundary(
  { i18nKey: "components.robots.RobotSummary.checkedIn.failed", small: true },
  _RobotSummaryCheckedin
);

export interface SummaryCardSectionProps {
  label: string;
  items: SummaryCardItemProps[];
}

interface RobotSummaryCardInfo {
  className?: string;
  variant: "freestanding" | "sidebar";
  short?: boolean;
  hideEmpty?: boolean;
  serial: string | undefined;
  customerSerial: string | undefined;
  priorityStatus: SummaryCardItemProps[];
  summarySections: SummaryCardSectionProps[];
  headlineInfo: RobotSummaryHeadlineInfo;
}

const _RobotSummaryCard: FunctionComponent<RobotSummaryCardInfo> = ({
  className = "",
  variant,
  hideEmpty = false,
  serial,
  customerSerial,
  priorityStatus,
  summarySections,
  headlineInfo,
}) => {
  const { t } = useTranslation();

  const name = valueOrLoading(customerSerial, t);
  const linkPath =
    serial && variant === "freestanding" ? getRobotPath(serial) : undefined;

  const content = useMemo(() => {
    if (variant === "freestanding") {
      return (
        <ul className="p-0 flex flex-col gap-2 mb-0">
          {priorityStatus.map((status, index) => (
            <SummaryCardItem {...status} key={index} />
          ))}
        </ul>
      );
    }
    return (
      <>
        {summarySections.map(({ label, items }) => (
          <Fragment key={label}>
            {!isEmpty(items) && (
              <div className="flex flex-col gap-2 pt-2">
                <Typography variant="subtitle2" className="font-bold">
                  {label}
                </Typography>
                {items.map((status, index) => (
                  <SummaryCardItem
                    {...status}
                    key={index}
                    hideEmpty={hideEmpty}
                  />
                ))}
              </div>
            )}
          </Fragment>
        ))}
      </>
    );
  }, [hideEmpty, priorityStatus, variant, summarySections]);

  return (
    <Card
      className={classes("flex flex-col", className, {
        "cursor-pointer": linkPath,
        "h-full": variant === "freestanding",
      })}
    >
      <CardContent component={linkPath ? Link : "div"} to={linkPath}>
        {variant === "freestanding" && (
          <div className="flex items-center gap-2">
            <RobotSummaryIcon {...headlineInfo} />
            <Typography
              variant="h4"
              className="text-ellipsis whitespace-nowrap overflow-hidden text-rtl text-lg"
            >
              {name}
            </Typography>
            <RobotSummaryText
              {...headlineInfo}
              className="text-ellipsis whitespace-nowrap overflow-hidden"
            />
          </div>
        )}
        {content}
      </CardContent>
    </Card>
  );
};

export const RobotSummaryCard = withErrorBoundary(
  { i18nKey: "views.fleet.robots.summary.failed" },
  _RobotSummaryCard
);
